/*
 * File: index.tsx
 * Desc: 表单输入搜索组件
 * File Created: 2019-10-17 10:38:23
 * Author: chenghao at <hao.cheng@karakal.com.cn>
 * ------
 * Copyright 2019 - present, karakal
 */
import React, { useState, useRef } from 'react';
import { Form, Input, AutoComplete, Button, Tag } from 'antd';
import { FormBaseProps } from '../form-input';
import { FormProps, FormComponentProps } from 'antd/lib/form';
import classNames from 'classnames';
import { SelectValue, OptionProps } from 'antd/lib/select';
import { debounce } from 'lodash';
import { PrototypeCheck } from '../utils';

import InputSearchTable, { FormInputSearchTableConfig } from '../input-search-table';

interface FormInputSearchProps extends FormBaseProps, FormProps {
    form: FormComponentProps['form'];
    /** 搜索调用的API */
    fetchApi?: Function;
    /** api的参数获取回调，通过value生成新的参数 */
    apiParamsCb?: (value: any) => any;
    /** 自定义Option children渲染 */
    optionRender?: (data: any) => React.ReactElement | string;
    /** 是否显示标签 */
    showTags?: boolean;
    /** 自定义Tag children渲染 */
    tagRender?: (data: any) => React.ReactElement | string;
    /** 自定义标签渲染的key */
    tagKey?: (data: any) => React.ReactElement | string;
    /** 标准化选择的value使之满足setFieldsValue */
    normalizeValue?: (value: SelectValue, option: any, fieldValue: any) => any;
    /** 是否可添加字符串 */
    strAddable?: boolean;
    /** 为tags的容器添加样式class */
    tagsClassName?: string;

    /** 选择后的回调函数 */
    onSelectCallBack?: Function;
    /** 每次输入后的回调函数 */
    onChange?: (value: any) => void;
    /** 默认输入框的值 */
    initialInputValue?: string | number;
    /** 删除标签回调函数 */
    tagCloseCB?: Function;
    /** 下拉表格配置 */
    tableConfig?: FormInputSearchTableConfig;
    withoutSetValue?: boolean;
}

const FormInputSearch = (props: FormInputSearchProps) => {
    const {
        label,
        labelCol,
        wrapperCol,
        className,
        tagsClassName,
        validator,
        field,
        initialValue,
        required,
        optionRender,
        normalizeValue,
        tagRender,
        tagKey,
        onSelectCallBack,
        onChange,
        initialInputValue,
        tagCloseCB,
        readOnly,
        showTags,
        withoutSetValue,
    } = props;
    const { getFieldDecorator, setFieldsValue, getFieldValue } = props.form;
    const [options, setOptions] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState<SelectValue>(initialInputValue as string);
    const [isFirst, setIsFirst] = useState(true);
    const tableRef = useRef<any>();

    /**
     * 选中时调用，注意设置value的时候，数组需要设置key
     * @param value
     * @param option
     */
    function onSelect(value: SelectValue, option?: OptionProps & { props?: any }, cb?: Function) {
        setInputValue(option && option.props['data-value']);
        setFieldsValue({
            [field]: normalizeValue ? normalizeValue(value, option, getFieldValue(field)) : value,
        });
        cb && cb();
    }

    const onSearch = debounce(function(value: string) {
        props.tableConfig && openTable();
        props.fetchApi &&
            props
                .fetchApi(props.apiParamsCb ? props.apiParamsCb(value) : value)
                .then((res: any) => {
                    setOptions(
                        res.map((data: any) => (
                            <AutoComplete.Option
                                key={data.key || data.value}
                                data-value={data.value}
                            >
                                {optionRender ? optionRender(data) : data.value}
                            </AutoComplete.Option>
                        ))
                    );
                });
    }, 500);

    const onInputChange = (value: SelectValue) => {
        setInputValue(value);
        setIsFirst(false);
        onChange && onChange(value);
    };

    /** 删除标签调用 */
    function onTagClose(_e: any, _i: number, _val?: any) {
        const fieldValue = getFieldValue(field);
        const filterValue: any[] = fieldValue.filter((_value: any) => _value.key !== _val.key);
        tagCloseCB
            ? tagCloseCB(_val, fieldValue, setFieldsValue, field)
            : setFieldsValue({
                  // filterValue为空时设置undefined触发表单校验
                  [field]: filterValue.length > 0 ? filterValue : undefined,
              });
    }

    function renderTags() {
        //
        return (
            <div style={{ lineHeight: 1 }} className={tagsClassName}>
                {getFieldValue(field).map((val: any, index: number) => (
                    <Tag
                        key={
                            tagKey
                                ? PrototypeCheck.isFunction(tagKey)
                                    ? tagKey(val)
                                    : val[tagKey as any]
                                : val.key
                        }
                        closable={!props.readOnly}
                        onClose={(e: any) => onTagClose(e, index, val)}
                    >
                        {/* 默认显示value属性 */}
                        {tagRender ? tagRender(val) : val.value}
                    </Tag>
                ))}
            </div>
        );
    }

    function onRowCB(normalizeValue: any, record: any, inputValue: number | string | null) {
        if (inputValue) setInputValue(inputValue);
        setFieldsValue({
            [field]: normalizeValue && normalizeValue(record, getFieldValue(field)),
        });
    }

    function openTable() {
        tableRef.current.setTableVisible(true);
    }

    function renderNormalRead() {
        return (
            !showTags &&
            readOnly &&
            (props.render ? props.render(getFieldValue(field)) : getFieldValue(field))
        );
    }

    return (
        <Form.Item
            {...{
                label,
                labelCol,
                wrapperCol,
            }}
            className={classNames('rac_form_inputs', className)}
        >
            {getFieldDecorator(field, {
                initialValue,
                rules: [
                    { required, message: `请输入${label}` },
                    {
                        validator: (rule: any, value: any, callback: Function) =>
                            validator ? validator(rule, value, callback) : callback(),
                    },
                ],
            })(<Input style={{ display: 'none' }} />)}
            {!props.readOnly && (
                <AutoComplete
                    onSelect={(value: SelectValue, option?: OptionProps) =>
                        onSelect(value, option, onSelectCallBack)
                    }
                    onChange={onInputChange}
                    onSearch={onSearch}
                    dataSource={options}
                    placeholder={`请输入${label}`}
                    optionLabelProp="value"
                    value={
                        inputValue ||
                        (isFirst &&
                            !withoutSetValue &&
                            PrototypeCheck.isString(getFieldValue(field)) &&
                            getFieldValue(field)) ||
                        ''
                    }
                >
                    <Input
                        suffix={
                            props.strAddable && (
                                <Button
                                    className="search-btn"
                                    style={{ marginRight: -12 }}
                                    type="primary"
                                    onClick={() => onSelect(inputValue)}
                                >
                                    确定
                                </Button>
                            )
                        }
                    />
                </AutoComplete>
            )}

            {props.tableConfig && (
                <InputSearchTable
                    onRowCB={onRowCB}
                    inputValue={inputValue}
                    tableConfig={{ ...props.tableConfig }}
                    ref={tableRef}
                />
            )}
            {props.showTags &&
                getFieldValue(field) &&
                (props.readOnly
                    ? props.render
                        ? props.render(getFieldValue(field))
                        : renderTags()
                    : renderTags())}
            {renderNormalRead()}
        </Form.Item>
    );
};

export default FormInputSearch;
